/**
 * How wide a character is, as a share of the font size.
 *
 * ONE average for every glyph was the first shape of this, and it was wrong in the direction that shows:
 * 0.55 em per character made "Commander maintenant" at the seller's chosen 30 px look like it needed 330 px
 * when it needed 300, so a button with room to spare shrank her text to 29.67 px — a control that quietly
 * disobeys. A capital is half again as wide as a lowercase and a space is a third of one; counting them
 * apart costs a lookup and keeps the ceiling out of the way until the text really cannot fit.
 *
 * These are advance widths for a bold humanist sans (Geist, Poppins, Inter — what the themes ship), rounded
 * UP: over-estimating shrinks the text a little more than strictly needed, under-estimating puts it back
 * outside the button, and only one of those two is visible to the shopper.
 */
const NARROW = new Set([...'iljtfrI|.,:;\'"!()[]-–—/\\']);
const WIDE = new Set([...'mwMW@%']);
const CHAR_WIDTH = {
    space: 0.27,
    narrow: 0.33,
    wide: 0.85,
    digit: 0.58,
    upper: 0.65,
    lower: 0.52,
    /** Anything else — CJK, emoji, symbols: full-width until proven otherwise. */
    other: 1,
};
/** The average used for characters we are told about but cannot see (`extraChars`). */
const AVG_CHAR = 0.55;
/** How wide `text` is, in `em` of its own font size. */
function estimateEm(text) {
    let total = 0;
    for (const ch of text) {
        if (ch === ' ' || ch === '\u00a0')
            total += CHAR_WIDTH.space;
        else if (NARROW.has(ch))
            total += CHAR_WIDTH.narrow;
        else if (WIDE.has(ch))
            total += CHAR_WIDTH.wide;
        else if (ch >= '0' && ch <= '9')
            total += CHAR_WIDTH.digit;
        else if (ch.toLowerCase() !== ch)
            total += CHAR_WIDTH.upper;
        else if (ch.toUpperCase() !== ch)
            total += CHAR_WIDTH.lower;
        else
            total += CHAR_WIDTH.other;
    }
    return total;
}
/** A number CSS can read: at most two decimals, no exponent, never zero as a divisor. */
const n2 = (value) => String(Math.round(value * 100) / 100);
/**
 * The style that keeps `label` on ONE line inside a button, shrinking it only as far as it must.
 *
 * @param label the text that must not break.
 * @param base  the font size the seller asked for, as CSS — a length, a `var(--…, 15px)`, or `1em` when the
 *              button's own size is whatever it inherits at this breakpoint.
 */
export function oneLineCta(label, base, geometry = {}) {
    return { whiteSpace: 'nowrap', fontSize: oneLineCtaSize(label, base, geometry) };
}
/**
 * The ceiling alone, as a CSS value — for a label that is styled from a STYLESHEET rather than inline.
 *
 * The sticky bar needs this: whether its button has a width at all depends on the device, so the rule has
 * to live inside a `@container` tier, and only in the tiers where there IS a container to measure.
 */
export function oneLineCtaSize(label, base, geometry = {}) {
    const { padPx = 0, padEm = 0, trackPx = 0, trackEm = 0, extraChars = 0 } = geometry;
    const text = label.trim();
    // Code POINTS, not UTF-16 units: an emoji in the label is one glyph, and `.length` would count it twice.
    const len = Math.max(1, [...text].length + Math.max(0, extraChars));
    const ems = Math.max(0.4, estimateEm(text) + Math.max(0, extraChars) * AVG_CHAR);
    // Solved for F, non-circular: ems·F + len·trackEm·F + padEm·F + padPx + len·trackPx ≤ width.
    const divisor = Math.max(0.01, ems + len * trackEm + padEm);
    const fixed = padPx + len * trackPx;
    return `min(${base}, calc((100cqi - ${n2(fixed)}px) / ${n2(divisor)}))`;
}
/** The whole rule body — `white-space` and the ceiling — for a stylesheet. */
export function oneLineCtaCss(label, base, geometry = {}) {
    return `white-space:nowrap;font-size:${oneLineCtaSize(label, base, geometry)}`;
}
/**
 * Turn a button into the container {@link oneLineCta} measures against.
 *
 * ONLY for a button whose inline size comes from the outside (`100%`, a `%` of the column). On a
 * `fit-content` button this would collapse it to nothing — and such a button grows to fit its own
 * one-line label anyway, so it never needed the cap.
 */
export const CTA_FIT_CONTAINER = { containerType: 'inline-size' };
//# sourceMappingURL=oneLineCta.js.map